// src/components/notify/console/NotifyInsightsPanel.js
// FlowSense AI · Delivery Intelligence analytics: recommendations, read rates by topic / role,
// hour-of-day heatmap, daily volume, alert-fatigue leaderboard, acknowledgement compliance.
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert, AlertDescription, AlertIcon, Badge, Box, Button, Flex, Grid, GridItem, Heading, HStack, Icon, Progress,
    Select, SimpleGrid, Skeleton, Stack, Text, Tooltip, useColorModeValue, VStack,
} from '@chakra-ui/react';
import { MdAutoAwesome, MdRefresh } from 'react-icons/md';
import * as api from '../../../services/notifyApi';
import { roleMeta } from '../../../config/roles';
import { categoryMeta, fmtDuration, pct, sourceMeta } from '../notifyFormat';

const LEVEL = { critical: 'error', warning: 'warning', info: 'info' };

function Panel({ title, children, right }) {
    const panel = useColorModeValue('white', 'gray.800');
    const border = useColorModeValue('gray.200', 'gray.700');
    return (
        <Box bg={panel} borderWidth="1px" borderColor={border} borderRadius="2xl" p={{ base: 4, md: 5 }} h="100%">
            <Flex justify="space-between" align="center" mb={3}><Heading size="sm">{title}</Heading>{right}</Flex>
            {children}
        </Box>
    );
}

function Bars({ rows, labelOf, colorOf }) {
    const muted = useColorModeValue('gray.500', 'gray.400');
    const max = Math.max(1, ...rows.map(r => r.total));
    if (!rows.length) return <Text fontSize="sm" color={muted}>No data yet.</Text>;
    return (
        <Stack spacing={2.5}>
            {rows.map(r => (
                <Box key={labelOf(r)}>
                    <Flex justify="space-between" fontSize="xs" mb={0.5}>
                        <Text fontWeight="600">{labelOf(r)}</Text>
                        <Text color={muted}>{r.total} sent · {pct(r.read_rate)} read</Text>
                    </Flex>
                    <Box position="relative" h="8px">
                        <Progress value={(r.total / max) * 100} size="sm" colorScheme="gray" borderRadius="full" position="absolute" inset={0} opacity={0.35} />
                        <Progress value={((r.total / max) * (r.read_rate || 0))} size="sm" colorScheme={colorOf(r)} borderRadius="full" position="absolute" inset={0} bg="transparent" aria-label={`${labelOf(r)} read rate ${pct(r.read_rate)}`} />
                    </Box>
                </Box>
            ))}
        </Stack>
    );
}

function HourHeatmap({ hours }) {
    const empty = useColorModeValue('gray.100', 'whiteAlpha.100');
    const muted = useColorModeValue('gray.500', 'gray.400');
    const maxTotal = Math.max(1, ...hours.map(h => h.total));
    return (
        <Box>
            <Grid templateColumns="repeat(24, 1fr)" gap="3px">
                {hours.map(h => {
                    const rate = h.read_within_1h_pct;
                    const color = rate === null ? empty : rate >= 60 ? 'green.400' : rate >= 35 ? 'yellow.400' : 'red.400';
                    return (
                        <Tooltip key={h.hour} label={`${String(h.hour).padStart(2, '0')}:00 — ${h.total} sent${rate !== null ? `, ${rate}% read within 1 h` : ''}`} hasArrow>
                            <Box h="34px" borderRadius="sm" bg={h.total ? color : empty} opacity={h.total ? 0.35 + 0.65 * (h.total / maxTotal) : 1} aria-label={`Hour ${h.hour}`} />
                        </Tooltip>
                    );
                })}
            </Grid>
            <Flex justify="space-between" mt={1} fontSize="2xs" color={muted}><Text>00:00</Text><Text>06:00</Text><Text>12:00</Text><Text>18:00</Text><Text>23:00</Text></Flex>
        </Box>
    );
}

function DailyChart({ daily }) {
    const line = useColorModeValue('#805AD5', '#B794F4');
    const read = useColorModeValue('#319795', '#4FD1C5');
    const muted = useColorModeValue('gray.500', 'gray.400');
    if (!daily.length) return <Text fontSize="sm" color={muted}>No data yet.</Text>;
    const W = 600; const H = 120; const pad = 6;
    const max = Math.max(1, ...daily.map(d => d.total));
    const x = (i) => (daily.length === 1 ? W / 2 : pad + (i * (W - pad * 2)) / (daily.length - 1));
    const y = (v) => H - pad - (v / max) * (H - pad * 2);
    const path = (key) => daily.map((d, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(d[key]).toFixed(1)}`).join(' ');
    return (
        <Box>
            <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="120" role="img" aria-label="Notifications per day">
                <path d={`${path('total')} L${x(daily.length - 1)},${H - pad} L${x(0)},${H - pad} Z`} fill={line} opacity="0.12" />
                <path d={path('total')} fill="none" stroke={line} strokeWidth="2.5" />
                <path d={path('read')} fill="none" stroke={read} strokeWidth="2" strokeDasharray="5 4" />
            </svg>
            <HStack spacing={4} fontSize="xs" color={muted}><HStack spacing={1}><Box w="10px" h="3px" bg={line} /><Text>Sent</Text></HStack><HStack spacing={1}><Box w="10px" h="3px" bg={read} /><Text>Read</Text></HStack><Text ml="auto">{daily[0].day} → {daily[daily.length - 1].day}</Text></HStack>
        </Box>
    );
}

function NotifyInsightsPanel() {
    const muted = useColorModeValue('gray.500', 'gray.400');
    const [days, setDays] = useState(14);
    const [state, setState] = useState({ loading: true, data: null, error: null });
    const isMounted = useRef(true);
    useEffect(() => () => { isMounted.current = false; }, []);

    const load = useCallback(async (signal) => {
        setState(s => ({ ...s, loading: true, error: null }));
        try {
            const data = await api.getInsights(days, signal);
            if (isMounted.current) setState({ loading: false, data, error: null });
        } catch (e) {
            if (e?.name !== 'AbortError' && isMounted.current) setState(s => ({ ...s, loading: false, error: e }));
        }
    }, [days]);

    useEffect(() => { const c = new AbortController(); load(c.signal); return () => c.abort(); }, [load]);

    const d = state.data;
    const roles = useMemo(() => (d?.by_role || []).map(r => ({ ...r, key: r.role })), [d]);

    if (state.error) return <Alert status="error" borderRadius="xl"><AlertIcon /><AlertDescription flex="1">{state.error.message}</AlertDescription><Button size="sm" onClick={() => load()}>Retry</Button></Alert>;

    return (
        <Stack spacing={4}>
            <Flex justify="space-between" align="center" gap={2} flexWrap="wrap">
                <HStack><Icon as={MdAutoAwesome} color="purple.500" /><Text fontWeight="700">Delivery Intelligence</Text>{d && <Badge colorScheme="purple" variant="subtle">{d.model}</Badge>}{d?.ai_share !== null && d && <Badge colorScheme="purple">{pct(d.ai_share)} sent by FlowSense AI</Badge>}</HStack>
                <HStack>
                    <Select size="sm" w="140px" value={days} onChange={(e) => setDays(Number(e.target.value))} aria-label="Period">
                        {[7, 14, 30, 90].map(n => <option key={n} value={n}>Last {n} days</option>)}
                    </Select>
                    <Button size="sm" variant="ghost" onClick={() => load()} isLoading={state.loading} aria-label="Refresh insights"><MdRefresh /></Button>
                </HStack>
            </Flex>

            {state.loading && !d ? <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>{[0, 1, 2, 3].map(i => <Skeleton key={i} h="220px" borderRadius="2xl" />)}</SimpleGrid> : d && (
                <>
                    {d.recommendations.length > 0 && (
                        <Panel title="FlowSense AI recommendations">
                            <VStack align="stretch" spacing={2}>
                                {d.recommendations.map((r, i) => <Alert key={i} status={LEVEL[r.level] || 'info'} borderRadius="lg" variant="left-accent" py={2}><AlertIcon /><AlertDescription fontSize="sm">{r.text}</AlertDescription></Alert>)}
                            </VStack>
                        </Panel>
                    )}
                    <Grid templateColumns={{ base: '1fr', lg: 'repeat(2, 1fr)' }} gap={4}>
                        <GridItem><Panel title="Read rate by topic"><Bars rows={d.by_category} labelOf={r => r.label} colorOf={r => categoryMeta(r.category).color} /></Panel></GridItem>
                        <GridItem><Panel title="Read rate by role"><Bars rows={roles} labelOf={r => `${roleMeta(r.role).label} (${r.people})`} colorOf={r => roleMeta(r.role).color} /></Panel></GridItem>
                        <GridItem><Panel title="When people read" right={d.best_hour && <Badge colorScheme="green">Best {d.best_hour.label}</Badge>}><HourHeatmap hours={d.by_hour} /><Text fontSize="xs" color={muted} mt={2}>Colour = share read within an hour · intensity = volume.</Text></Panel></GridItem>
                        <GridItem><Panel title="Volume"><DailyChart daily={d.daily} /></Panel></GridItem>
                        <GridItem>
                            <Panel title="Acknowledgements">
                                <SimpleGrid columns={2} spacing={3}>
                                    <Box><Text fontSize="xs" color={muted}>Compliance</Text><Text fontSize="2xl" fontWeight="800" color={d.ack.compliance !== null && d.ack.compliance < 80 ? 'red.400' : undefined}>{pct(d.ack.compliance)}</Text></Box>
                                    <Box><Text fontSize="xs" color={muted}>Avg time to ack</Text><Text fontSize="2xl" fontWeight="800">{fmtDuration(d.ack.avg_ack_sec)}</Text></Box>
                                    <Box><Text fontSize="xs" color={muted}>Required</Text><Text fontWeight="700">{d.ack.required}</Text></Box>
                                    <Box><Text fontSize="xs" color={muted}>Escalated to supervisors</Text><Text fontWeight="700" color={d.ack.escalated ? 'red.400' : undefined}>{d.ack.escalated}</Text></Box>
                                </SimpleGrid>
                            </Panel>
                        </GridItem>
                        <GridItem>
                            <Panel title="Alert fatigue (7 days)">
                                {d.fatigue.length === 0 ? <Text fontSize="sm" color={muted}>No data yet.</Text> : (
                                    <Stack spacing={1.5}>
                                        {d.fatigue.map(f => (
                                            <Flex key={f.user_id} justify="space-between" fontSize="sm" align="center">
                                                <HStack spacing={2}><Badge colorScheme={roleMeta(f.role).color} variant="subtle">{roleMeta(f.role).short}</Badge><Text>{f.name}</Text></HStack>
                                                <Text color={f.unread > f.total / 2 ? 'red.400' : muted}>{f.total} · {f.unread} unread</Text>
                                            </Flex>
                                        ))}
                                    </Stack>
                                )}
                            </Panel>
                        </GridItem>
                    </Grid>
                    <Panel title="By source">
                        <Flex gap={2} flexWrap="wrap">
                            {d.by_source.map(s => { const m = sourceMeta(s.source); return <Badge key={s.source} colorScheme={m.ai ? 'purple' : 'gray'} px={2} py={1} borderRadius="md">{m.ai && <Icon as={MdAutoAwesome} mr={1} />}{m.label}: {s.total} · {pct(s.read_rate)} read</Badge>; })}
                        </Flex>
                    </Panel>
                </>
            )}
        </Stack>
    );
}

export default memo(NotifyInsightsPanel);
